'use client';

import { useRef, useState } from 'react';
import { pluralize } from '@bridge/utils';

import { Badge } from './Badge';
import { Body } from '../typography';

export interface TagInputProps {
  /** Current list of tags */
  value: string[];
  /** Called when tags change (add/remove) */
  onChange: (next: string[]) => void;
  /** Input element id */
  id?: string;
  /** Disable interaction */
  disabled?: boolean;
  /** Placeholder when no tags are present */
  placeholder?: string;
  /** Case-insensitive deduplication (default: true) */
  caseInsensitive?: boolean;
  /** Helper text or error shown below the input */
  helperText?: string;
  /** Focus the input on mount */
  autoFocus?: boolean;
}

/**
 * Tag/chip-based input for managing a list of string values.
 *
 * Features:
 * - Type a value and press Enter or comma to add as a tag
 * - Backspace on empty input removes the last tag
 * - Paste comma/newline-delimited lists to add multiple at once
 * - Case-insensitive deduplication by default
 * - Commits draft on blur (prevents silent data loss)
 * - Each tag has a × button to remove it
 *
 * @example
 * ```tsx
 * <TagInput
 *   value={countries}
 *   onChange={setCountries}
 *   placeholder="Add country codes..."
 * />
 * ```
 */
export function TagInput({
  value,
  onChange,
  id,
  disabled,
  placeholder = 'Type a value and press Enter...',
  caseInsensitive = true,
  helperText,
  autoFocus,
}: TagInputProps) {
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  function isDuplicate(entry: string): boolean {
    if (caseInsensitive) {
      return value.some((v) => v.toLowerCase() === entry.toLowerCase());
    }
    return value.includes(entry);
  }

  function addEntry(raw: string) {
    const trimmed = raw.trim();
    if (!trimmed) return;
    if (isDuplicate(trimmed)) return;
    onChange([...value, trimmed]);
  }

  function removeEntry(index: number) {
    onChange(value.filter((_, i) => i !== index));
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addEntry(draft);
      setDraft('');
    }
    if (e.key === 'Backspace' && draft === '' && value.length > 0) {
      removeEntry(value.length - 1);
    }
  }

  function handlePaste(e: React.ClipboardEvent<HTMLInputElement>) {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    const entries = pasted.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    const existing = caseInsensitive ? new Set(value.map((v) => v.toLowerCase())) : new Set(value);
    const unique = entries.filter((entry) => {
      const key = caseInsensitive ? entry.toLowerCase() : entry;
      if (existing.has(key)) return false;
      existing.add(key);
      return true;
    });
    if (unique.length > 0) {
      onChange([...value, ...unique]);
    }
    setDraft('');
  }

  const countLabel = `${value.length} ${pluralize(value.length, 'value')}`;

  return (
    <div className="flex flex-col gap-1.5">
      <div
        className="flex flex-wrap gap-1.5 rounded-md border border-border-secondary bg-background px-3 py-2 min-h-[38px] cursor-text"
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((entry, i) => (
          // Stop the click from reaching the wrapper's focus handler so the tag text stays selectable.
          <Badge
            className="gap-1 pl-2 pr-1 select-text"
            key={`${entry}-${i}`}
            onClick={(e) => e.stopPropagation()}
            variant="default"
          >
            {entry}
            {!disabled && (
              <button
                className="ml-0.5 rounded-full hover:bg-background-tertiary p-0.5 leading-none"
                onClick={(e) => { e.stopPropagation(); removeEntry(i); }}
                type="button"
              >
                ×
              </button>
            )}
          </Badge>
        ))}
        <input
          className="flex-1 min-w-[120px] outline-none bg-transparent text-sm placeholder:text-content-tertiary"
          autoFocus={autoFocus}
          disabled={disabled}
          id={id}
          onBlur={() => { addEntry(draft); setDraft(''); }}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={value.length === 0 ? placeholder : ''}
          ref={inputRef}
          type="text"
          value={draft}
        />
      </div>
      <Body className="text-content-tertiary" size="xs">
        {helperText ?? `${countLabel} — press Enter or comma to add, Backspace to remove last`}
      </Body>
    </div>
  );
}
